import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, Filter, MoreVertical, RefreshCw,
  Image as ImageIcon, MapPin, Calendar, Images, X
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';
import { MultiMediaUploader } from '../../components/ui/MultiMediaUploader';

const categories = ['Front Elevation', 'Office Partitions', 'Home Glass Work', 'Shower Enclosures', 'Balcony and Stair Glass Work', 'Sliding Windows and Doors', 'ACP/HPL Facade'];

const defaultProject = {
  id: '', title: '', category: 'Front Elevation', description: '', short_description: '',
  image: '', images: [], videos: [], before_image: '', after_image: '',
  location: '', completion_date: '', featured: false, show_on_homepage: true,
  order: 0, is_active: true
};

export const ProjectsManager = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await apiService.getProjects(token);
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.title) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await apiService.createProject(token, item);
        toast.success('Project created successfully!');
      } else {
        await apiService.updateProject(token, item.id, item);
        toast.success('Project updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving project');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteProject(token, deleteDialog.item.id);
      toast.success('Project deleted');
      setDeleteDialog({ open: false, item: null });
      fetchProjects();
    } catch (error) {
      toast.error('Error deleting project');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, images: item.images || [], videos: item.videos || [], isNew: false } : { ...defaultProject, id: `project-${Date.now()}`, order: projects.length + 1, images: [], videos: [], isNew: true }
    });
  };

  const filtered = projects.filter(p => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="space-y-6" data-testid="projects-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Gallery / Projects</h1>
          <p className="text-slate-500">Showcase your completed work</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchProjects}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No projects found</p>
          <Button onClick={() => openEditModal()} variant="outline" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Add Project</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((project) => {
            const totalImages = [project.image, project.before_image, project.after_image, ...(project.images || [])].filter(Boolean).length;
            return (
            <Card key={project.id} className={`overflow-hidden ${!project.is_active ? 'opacity-60' : ''}`}>
              <div className="aspect-video relative bg-slate-100">
                <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {project.featured && <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded">Featured</span>}
                  {!project.is_active && <span className="px-2 py-1 bg-slate-500 text-white text-xs rounded">Inactive</span>}
                </div>
                {/* Image count badge */}
                {totalImages > 1 && (
                  <div className="absolute bottom-2 left-2">
                    <span className="px-2 py-1 bg-black/70 text-white text-xs rounded flex items-center gap-1">
                      <Images className="w-3 h-3" />
                      {totalImages} images
                    </span>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="secondary" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(project)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: project })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-sky-600 font-medium">{project.category}</span>
                  <span className="text-xs text-slate-400">#{project.order}</span>
                </div>
                <h3 className="font-semibold text-slate-900 line-clamp-1">{project.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1">{project.short_description || project.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  {project.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{project.location}</span>}
                  {project.completion_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{project.completion_date}</span>}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Project' : 'Edit Project'}</DialogTitle>
            <DialogDescription>Fill in the project details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-6 py-4">
              <ImageUploader
                label="Main Image *"
                value={editModal.item.image}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, image: url } })}
                aspectRatio={16/9}
                token={token}
              />

              {/* Before/After Images */}
              <div className="grid grid-cols-2 gap-4">
                <ImageUploader
                  label="Before Image (Optional)"
                  value={editModal.item.before_image || ''}
                  onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, before_image: url } })}
                  aspectRatio={16/9}
                  token={token}
                />
                <ImageUploader
                  label="After Image (Optional)"
                  value={editModal.item.after_image || ''}
                  onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, after_image: url } })}
                  aspectRatio={16/9}
                  token={token}
                />
              </div>

              {/* Additional Images */}
              <MultiMediaUploader
                label="Project Gallery (Images & Videos)"
                images={editModal.item.images || []}
                videos={editModal.item.videos || []}
                onImagesChange={(images) => setEditModal({ ...editModal, item: { ...editModal.item, images } })}
                onVideosChange={(videos) => setEditModal({ ...editModal, item: { ...editModal.item, videos } })}
                maxImages={15}
                maxVideos={5}
                showVideos={true}
                token={token}
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project ID *</Label>
                  <Input value={editModal.item.id} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, id: e.target.value } })} disabled={!editModal.item.isNew} />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={editModal.item.category} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, category: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Project Title *</Label>
                <Input
                  value={editModal.item.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    const updates = { title };
                    if (editModal.item.isNew) updates.id = generateSlug(title);
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Modern Office Glass Partition"
                />
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Input value={editModal.item.short_description || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, short_description: e.target.value } })} placeholder="Brief description" maxLength={100} />
              </div>

              <div className="space-y-2">
                <Label>Full Description *</Label>
                <Textarea value={editModal.item.description} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, description: e.target.value } })} placeholder="Detailed project description..." rows={3} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={editModal.item.location || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, location: e.target.value } })} placeholder="e.g., Faridabad" />
                </div>
                <div className="space-y-2">
                  <Label>Completion Date</Label>
                  <Input value={editModal.item.completion_date || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, completion_date: e.target.value } })} placeholder="e.g., 2024" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input type="number" value={editModal.item.order} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })} />
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_active} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })} />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.featured} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, featured: checked } })} />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.show_on_homepage} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })} />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteDialog.item?.title}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsManager;
